import type {
  MermaidRenderOptions,
  MermaidRenderRequest,
  MermaidRenderResult,
  MermaidRenderTheme,
  MermaidValidationResult,
} from '../mermaid/renderer/types';
import { MERMAID_MAX_EDGES, MERMAID_MAX_TEXT_SIZE } from './classic-mermaid';

export const MERMAID_CANVAS_RENDER_CONTRACT_VERSION = 1;
export const MERMAID_CANVAS_MAX_LINES = 5_000;
export const MERMAID_CANVAS_CACHE_LIMIT = 64;
export const MERMAID_CANVAS_DISTANT_ZOOM_THRESHOLD = 0.15;

const DEFAULT_FONT_FAMILY = 'IBM Plex Mono';
const DEFAULT_INTRINSIC_SIZE = { width: 480, height: 320 };
const AUTO_SIZE_PADDING = 32;
const AUTO_SIZE_MIN = { width: 320, height: 200 };
const AUTO_SIZE_MAX = { width: 1200, height: 900 };

export type MermaidCanvasFitMode = 'content' | 'width' | 'free';

export type MermaidCanvasThemeMode = 'auto' | 'light' | 'dark';

export function resolveMermaidCanvasTheme(
  themeMode: MermaidCanvasThemeMode,
  prefersDark: boolean
): MermaidRenderTheme {
  if (themeMode === 'dark' || (themeMode === 'auto' && prefersDark)) {
    return 'dark';
  }
  return 'default';
}

export type MermaidCanvasSize = {
  width: number;
  height: number;
};

export type MermaidCanvasIssueCode =
  | 'disposed'
  | 'empty'
  | 'source_too_large'
  | 'too_many_lines'
  | 'too_many_edges'
  | 'invalid_syntax'
  | 'render_failed';

export type MermaidCanvasIssue = {
  code: MermaidCanvasIssueCode;
  message: string;
};

export type MermaidCanvasRenderInput = {
  source: string;
  theme?: MermaidRenderTheme;
  fontFamily?: string;
  visible?: boolean;
  zoom?: number;
};

export type MermaidCanvasReadyResult = {
  status: 'ready';
  svg: string;
  cacheKey: string;
  fromCache: boolean;
  intrinsicSize: MermaidCanvasSize;
};

export type MermaidCanvasRenderResult =
  | MermaidCanvasReadyResult
  | {
      status: 'empty' | 'deferred' | 'superseded';
      cacheKey?: string;
    }
  | {
      status: 'degraded';
      svg: string;
      cacheKey: string;
      intrinsicSize: MermaidCanvasSize;
    }
  | {
      status: 'invalid' | 'error';
      issue: MermaidCanvasIssue;
      cacheKey?: string;
      lastValid?: MermaidCanvasReadyResult;
    };

type MermaidCanvasCacheEntry = {
  svg: string;
  intrinsicSize: MermaidCanvasSize;
};

type MermaidCanvasRuntimeDependencies = {
  render?: (request: MermaidRenderRequest) => Promise<MermaidRenderResult>;
  validate?: (
    request: MermaidRenderRequest
  ) => Promise<MermaidValidationResult>;
  digest?: (value: string) => Promise<string>;
};

const renderCache = new Map<string, MermaidCanvasCacheEntry>();

function boundedMessage(message: string) {
  return message.replace(/\s+/g, ' ').trim().slice(0, 400);
}

function estimateEdgeCount(source: string) {
  const matches = source.match(
    /(?:--+>|==+>|-\.->|--+|==+|<\|--|--\||o--|--o|\*--|--\*|-\)-|->>|-->>|--x|--\)|\+--)/g
  );
  return matches?.length ?? 0;
}

function preflightSource(source: string): MermaidCanvasIssue | null {
  if (!source.trim()) {
    return { code: 'empty', message: 'Mermaid source is empty.' };
  }

  if (source.length > MERMAID_MAX_TEXT_SIZE) {
    return {
      code: 'source_too_large',
      message: `Mermaid source exceeds ${MERMAID_MAX_TEXT_SIZE} characters.`,
    };
  }

  const lineCount = source.split(/\r?\n/).length;
  if (lineCount > MERMAID_CANVAS_MAX_LINES) {
    return {
      code: 'too_many_lines',
      message: `Mermaid source exceeds ${MERMAID_CANVAS_MAX_LINES} lines.`,
    };
  }

  const edgeCount = estimateEdgeCount(source);
  if (edgeCount > MERMAID_MAX_EDGES) {
    return {
      code: 'too_many_edges',
      message: `Mermaid source exceeds ${MERMAID_MAX_EDGES} edges.`,
    };
  }

  return null;
}

function stableConfig(input: MermaidCanvasRenderInput): MermaidRenderOptions {
  return {
    fastText: true,
    svgOnly: true,
    theme: input.theme ?? 'default',
    fontFamily: input.fontFamily ?? DEFAULT_FONT_FAMILY,
  };
}

async function sha256(value: string) {
  if (globalThis.crypto?.subtle) {
    const bytes = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), byte =>
      byte.toString(16).padStart(2, '0')
    ).join('');
  }

  // Deterministic non-cryptographic fallback for runtimes without WebCrypto.
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return 'fnv1a-' + (hash >>> 0).toString(16).padStart(8, '0');
}

async function defaultValidate(request: MermaidRenderRequest) {
  const { validateMermaidSource } = await import('./bridge');
  return validateMermaidSource(request);
}

async function defaultRender(request: MermaidRenderRequest) {
  const { renderMermaidSvg } = await import('./bridge');
  return renderMermaidSvg(request);
}

async function buildCacheKey(
  input: MermaidCanvasRenderInput,
  digest: (value: string) => Promise<string>
) {
  const options = stableConfig(input);
  return digest(
    JSON.stringify({
      contract: MERMAID_CANVAS_RENDER_CONTRACT_VERSION,
      source: input.source,
      theme: options.theme,
      fontFamily: options.fontFamily,
      maxTextSize: MERMAID_MAX_TEXT_SIZE,
      maxEdges: MERMAID_MAX_EDGES,
    })
  );
}

function readNumericLength(value: string | null) {
  if (!value) return null;
  const match = /^\s*(\d+(?:\.\d+)?)(?:px)?\s*$/.exec(value);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function normalizeSvgForCanvas(svg: string): {
  svg: string;
  intrinsicSize: MermaidCanvasSize;
} {
  if (
    typeof DOMParser === 'undefined' ||
    typeof XMLSerializer === 'undefined'
  ) {
    return { svg, intrinsicSize: DEFAULT_INTRINSIC_SIZE };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== 'svg') {
    throw new Error('Sanitized Mermaid output is not an SVG document.');
  }

  const viewBox = root
    .getAttribute('viewBox')
    ?.trim()
    .split(/[\s,]+/)
    .map(Number);
  const width = readNumericLength(root.getAttribute('width'));
  const height = readNumericLength(root.getAttribute('height'));

  let intrinsicWidth =
    viewBox?.length === 4 && Number.isFinite(viewBox[2]) && viewBox[2] > 0
      ? viewBox[2]
      : width;
  let intrinsicHeight =
    viewBox?.length === 4 && Number.isFinite(viewBox[3]) && viewBox[3] > 0
      ? viewBox[3]
      : height;

  intrinsicWidth ??= DEFAULT_INTRINSIC_SIZE.width;
  intrinsicHeight ??= DEFAULT_INTRINSIC_SIZE.height;

  if (!root.hasAttribute('viewBox')) {
    root.setAttribute('viewBox', `0 0 ${intrinsicWidth} ${intrinsicHeight}`);
  }
  root.setAttribute('width', '100%');
  root.setAttribute('height', '100%');
  root.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  return {
    svg: new XMLSerializer().serializeToString(root).trim(),
    intrinsicSize: {
      width: intrinsicWidth,
      height: intrinsicHeight,
    },
  };
}

function getCached(key: string) {
  const entry = renderCache.get(key);
  if (!entry) return null;
  renderCache.delete(key);
  renderCache.set(key, entry);
  return entry;
}

function setCached(key: string, entry: MermaidCanvasCacheEntry) {
  renderCache.delete(key);
  renderCache.set(key, entry);
  while (renderCache.size > MERMAID_CANVAS_CACHE_LIMIT) {
    const oldest = renderCache.keys().next().value as string | undefined;
    if (!oldest) break;
    renderCache.delete(oldest);
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function getMermaidCanvasSizeSuggestion({
  fitMode,
  intrinsicSize,
  currentSize,
  viewportSize,
}: {
  fitMode: MermaidCanvasFitMode;
  intrinsicSize: MermaidCanvasSize;
  currentSize: MermaidCanvasSize;
  viewportSize?: MermaidCanvasSize;
}): MermaidCanvasSize {
  if (fitMode === 'free') {
    return currentSize;
  }

  const viewportMax = viewportSize
    ? {
        width: Math.max(
          AUTO_SIZE_MIN.width,
          Math.min(AUTO_SIZE_MAX.width, viewportSize.width * 0.8)
        ),
        height: Math.max(
          AUTO_SIZE_MIN.height,
          Math.min(AUTO_SIZE_MAX.height, viewportSize.height * 0.8)
        ),
      }
    : AUTO_SIZE_MAX;

  const ratio =
    intrinsicSize.width > 0 && intrinsicSize.height > 0
      ? intrinsicSize.width / intrinsicSize.height
      : DEFAULT_INTRINSIC_SIZE.width / DEFAULT_INTRINSIC_SIZE.height;

  if (fitMode === 'width') {
    const width = clamp(
      currentSize.width,
      AUTO_SIZE_MIN.width,
      viewportMax.width
    );
    return {
      width,
      height: clamp(
        width / ratio + AUTO_SIZE_PADDING,
        AUTO_SIZE_MIN.height,
        viewportMax.height
      ),
    };
  }

  return {
    width: clamp(
      intrinsicSize.width + AUTO_SIZE_PADDING,
      AUTO_SIZE_MIN.width,
      viewportMax.width
    ),
    height: clamp(
      intrinsicSize.height + AUTO_SIZE_PADDING,
      AUTO_SIZE_MIN.height,
      viewportMax.height
    ),
  };
}

export function clearMermaidCanvasRenderCache() {
  renderCache.clear();
}

export function getMermaidCanvasRenderCacheSize() {
  return renderCache.size;
}

export class MermaidCanvasRenderRuntime {
  private readonly renderSvg: (
    request: MermaidRenderRequest
  ) => Promise<MermaidRenderResult>;
  private readonly validateSource: (
    request: MermaidRenderRequest
  ) => Promise<MermaidValidationResult>;

  private readonly digest: (value: string) => Promise<string>;

  private generation = 0;
  private disposed = false;
  private lastValid: MermaidCanvasReadyResult | undefined;

  constructor(dependencies: MermaidCanvasRuntimeDependencies = {}) {
    this.renderSvg = dependencies.render ?? defaultRender;
    this.validateSource = dependencies.validate ?? defaultValidate;
    this.digest = dependencies.digest ?? sha256;
  }

  dispose() {
    this.disposed = true;
    this.generation++;
    this.lastValid = undefined;
  }

  async render(
    input: MermaidCanvasRenderInput
  ): Promise<MermaidCanvasRenderResult> {
    const generation = ++this.generation;

    if (this.disposed) {
      return {
        status: 'error',
        issue: { code: 'disposed', message: 'Mermaid renderer is disposed.' },
      };
    }

    const preflight = preflightSource(input.source);
    if (preflight?.code === 'empty') {
      return { status: 'empty' };
    }
    if (preflight) {
      return {
        status: 'invalid',
        issue: preflight,
        lastValid: this.lastValid,
      };
    }

    const cacheKey = await buildCacheKey(input, this.digest);
    if (generation !== this.generation || this.disposed) {
      return { status: 'superseded', cacheKey };
    }

    const cached = getCached(cacheKey);
    if (cached) {
      const ready: MermaidCanvasReadyResult = {
        status: 'ready',
        svg: cached.svg,
        cacheKey,
        fromCache: true,
        intrinsicSize: cached.intrinsicSize,
      };
      this.lastValid = ready;
      return ready;
    }

    if (input.visible === false) {
      return { status: 'deferred', cacheKey };
    }

    if (
      typeof input.zoom === 'number' &&
      input.zoom < MERMAID_CANVAS_DISTANT_ZOOM_THRESHOLD
    ) {
      if (this.lastValid?.cacheKey === cacheKey) {
        return {
          status: 'degraded',
          svg: this.lastValid.svg,
          cacheKey,
          intrinsicSize: this.lastValid.intrinsicSize,
        };
      }
      return { status: 'deferred', cacheKey };
    }

    const options = stableConfig(input);
    const validation = await this.validateSource({
      code: input.source,
      options,
    });
    if (generation !== this.generation || this.disposed) {
      return { status: 'superseded', cacheKey };
    }
    if (!validation.valid) {
      return {
        status: 'invalid',
        issue: {
          code: 'invalid_syntax',
          message: boundedMessage(validation.error),
        },
        cacheKey,
        lastValid: this.lastValid,
      };
    }

    try {
      const rendered = await this.renderSvg({
        code: input.source,
        options,
      });
      if (generation !== this.generation || this.disposed) {
        return { status: 'superseded', cacheKey };
      }

      const normalized = normalizeSvgForCanvas(rendered.svg);
      setCached(cacheKey, normalized);

      const ready: MermaidCanvasReadyResult = {
        status: 'ready',
        svg: normalized.svg,
        cacheKey,
        fromCache: false,
        intrinsicSize: normalized.intrinsicSize,
      };
      this.lastValid = ready;
      return ready;
    } catch (error) {
      return {
        status: 'error',
        issue: {
          code: 'render_failed',
          message: boundedMessage(
            error instanceof Error ? error.message : String(error)
          ),
        },
        cacheKey,
        lastValid: this.lastValid,
      };
    }
  }
}
